import { Note, NotesSection, PlayerNote, SongTrack } from '~/interfaces';
import getPlayerNoteDistance from '~/modules/game-engine/helpers/get-player-note-distance';
import isNotesSection from '~/modules/songs/utils/is-notes-section';

/** How a singer did note by note, as opposed to the points those notes were worth. */
export interface NoteStats {
  perfect: number;
  /** On pitch, but not held cleanly enough to count as perfect. */
  good: number;
  /**
   * Recognisably the right note but outside the tolerance, so it scored nothing. Counted apart
   * from `missed` to tell "nearly" from "nowhere near", but it is not a hit.
   */
  ok: number;
  missed: number;
  /** Notes this singer was asked to sing, whether or not they sang them at all. */
  total: number;
  /** Longest run of notes hit without missing one. */
  maxCombo: number;
  /**
   * How well the track was sung, 0-1 - every note weighed by how well it was hit rather than counted
   * flat as a hit or a miss. See `QUALITY_WEIGHTS`.
   */
  accuracy: number;
}

export const emptyNoteStats = (): NoteStats => ({
  perfect: 0,
  good: 0,
  ok: 0,
  missed: 0,
  total: 0,
  maxCombo: 0,
  accuracy: 0,
});

export type NoteQuality = 'perfect' | 'good' | 'ok' | 'missed';

/**
 * How well one note was sung. The single definition of that - the post-song stats and the live
 * feedback during the song both read it, so they can never disagree about what "perfect" meant.
 *
 * Read through `getPlayerNoteDistance`, which is what the score uses, so both agree on rap and
 * freestyle: those are not judged on pitch at all, and reading `note.distance` directly counted
 * them as missed while the score awarded them in full.
 *
 * `distance` is signed, so how far out a note was is its magnitude - comparing the raw value would
 * count a note five semitones flat as a near miss. It is also already zero for anything inside the
 * tolerance the singers chose, which is why the bands have to be relative to that tolerance: a
 * fixed two semitones was narrower than the tolerance itself on every setting but Hard, so nothing
 * could ever land between "hit" and "missed" and the `ok` count was permanently zero.
 */
export const classifyNote = (note: PlayerNote, tolerance: number): NoteQuality => {
  const distance = getPlayerNoteDistance(note);

  if (Math.abs(distance) > tolerance + 1) return 'missed';
  if (note.isPerfect) return 'perfect';

  return distance === 0 ? 'good' : 'ok';
};

/** Whether a quality is one that scored. `ok` is close, but the engine awards it nothing. */
export const isHit = (quality: NoteQuality) => quality === 'perfect' || quality === 'good';

// What each judgement is worth towards the accuracy. Only a note held cleanly enough to be perfect
// is the whole of singing it: `good` was on pitch but unsteady and `ok` never reached the tolerance
// at all, so each earns part of the note. A missed note earns none of it, so a track half sung
// cannot read as nearly right on the strength of the near ones.
export const QUALITY_WEIGHTS: Record<NoteQuality, number> = { perfect: 1, good: 0.8, ok: 0.4, missed: 0 };

// Worked out from the counts rather than read off a stored `accuracy`, so a play recorded before
// the weighting existed is read by today's rule instead of keeping its old share of notes hit.
export const accuracyOf = ({ perfect, good, ok, total }: Pick<NoteStats, 'perfect' | 'good' | 'ok' | 'total'>) =>
  total > 0
    ? Math.min(1, (perfect * QUALITY_WEIGHTS.perfect + good * QUALITY_WEIGHTS.good + ok * QUALITY_WEIGHTS.ok) / total)
    : 0;

// The share of the track that scored at all, which is what an achievement about hitting notes
// counts - a weighted accuracy would quietly move every one of those thresholds.
export const hitRateOf = ({ perfect, good, total }: Pick<NoteStats, 'perfect' | 'good' | 'total'>) =>
  total > 0 ? Math.min(1, (perfect + good) / total) : 0;

const QUALITY_RANK: Record<NoteQuality, number> = { missed: 0, ok: 1, good: 2, perfect: 3 };

/**
 * The best quality reached on each note the singer actually attempted, keyed by the note's start
 * beat, plus the order those notes were first sung in.
 *
 * `PlayerNote[]` is not one entry per note. `appendFrequencyToPlayerNotes` starts a new entry every
 * time the pitch moves, so one note held a little unsteadily arrives here as several - and counting
 * those as notes let accuracy climb past 100% and inflated every combo. Eight segments on a
 * one-note track used to report an accuracy of 4.
 *
 * Best attempt wins, because the question each of these figures asks is whether the note was hit,
 * not whether every instant of it was.
 */
export const bestQualityByNote = (playerNotes: PlayerNote[], tolerance: number) => {
  const best = new Map<number, NoteQuality>();
  const order: number[] = [];

  for (const playerNote of playerNotes) {
    const start = playerNote.note.start;
    const quality = classifyNote(playerNote, tolerance);
    const known = best.get(start);

    if (known === undefined) {
      best.set(start, quality);
      order.push(start);
    } else if (QUALITY_RANK[quality] > QUALITY_RANK[known]) {
      best.set(start, quality);
    }
  }

  return { best, order };
};

/** One quality per attempted note, in the order they were sung. */
export const bestQualityPerNote = (playerNotes: PlayerNote[], tolerance: number): NoteQuality[] => {
  const { best, order } = bestQualityByNote(playerNotes, tolerance);

  return order.map((start) => best.get(start)!);
};

export interface NoteScope {
  /**
   * Notes ending after this beat are still to come, so they are nobody's miss yet. Left out once
   * the song is over, when every note of the track has had its chance.
   */
  upToBeat?: number;
  /** Whether a section is this singer's to sing - pass the mic hands the track between them. */
  ownsSection?: (section: NotesSection) => boolean;
}

/** The notes of the track this singer is answerable for, in the order they are sung. */
export const notesInScope = (track: SongTrack | undefined, { upToBeat, ownsSection }: NoteScope = {}): Note[] => {
  if (!track) return [];

  return track.sections
    .filter(isNotesSection)
    .filter((section) => ownsSection?.(section) ?? true)
    .flatMap((section) => section.notes)
    .filter((note) => upToBeat === undefined || note.start + note.length <= upToBeat);
};

/**
 * One quality per note of the track, in the order they are sung - a note with nothing sung against
 * it is a miss.
 *
 * Driven by the track rather than by what was sung, because `PlayerNote[]` holds only the notes the
 * singer made a sound during: silence appends nothing at all, since `appendFrequencyToPlayerNotes`
 * drops a frequency of zero and that is what the input reports when it hears nothing. Walking the
 * sung notes alone kept a run alive through any length of silence, and merged the runs either side
 * of a passage nobody sang into one.
 *
 * Without a track there is nothing to walk, so the notes attempted are all there is to go on.
 */
export const qualityPerTrackNote = (
  playerNotes: PlayerNote[],
  track: SongTrack | undefined,
  tolerance: number,
  scope: NoteScope = {},
): NoteQuality[] => {
  if (!track) return bestQualityPerNote(playerNotes, tolerance);

  const { best } = bestQualityByNote(playerNotes, tolerance);

  return notesInScope(track, scope).map((note) => best.get(note.start) ?? 'missed');
};

export const calculateNoteStats = (
  playerNotes: PlayerNote[],
  track: SongTrack | undefined,
  tolerance: number,
  scope: NoteScope = {},
): NoteStats => {
  // The same notes the qualities are counted from, so the two can never disagree about what the
  // singer was asked to sing - which is what makes a share of them meaningful.
  const total = notesInScope(track, scope).length;

  const stats = { ...emptyNoteStats(), total };
  let combo = 0;

  for (const quality of qualityPerTrackNote(playerNotes, track, tolerance, scope)) {
    if (quality === 'perfect') stats.perfect++;
    else if (quality === 'good') stats.good++;
    else if (quality === 'ok') stats.ok++;

    // A note that scored nothing ends the run, whether it was close or nowhere near.
    if (isHit(quality)) {
      combo++;
    } else {
      stats.maxCombo = Math.max(stats.maxCombo, combo);
      combo = 0;
    }
  }

  stats.maxCombo = Math.max(stats.maxCombo, combo);

  // Notes never sung at all count as missed, so a song abandoned halfway cannot read as perfect.
  // The near ones are already counted under `ok`, so the four add up to the track.
  stats.missed = Math.max(0, total - stats.perfect - stats.good - stats.ok);
  stats.accuracy = accuracyOf(stats);

  return stats;
};
